/** Business profile — what the company tells Signals about its business.
 *
 * The chat uses it to pick which market prices to test against the company's
 * figures (signals-copilot get_signal_hints). Nothing here is required: a
 * field left empty only means Signals ranks the signals without it. Commodities
 * are picked from the series Signals actually has, never typed, because a word
 * it cannot match would quietly test nothing.
 *
 * Each field saves on its own (PUT with just that field), and the server
 * records who changed what in the company's audit log.
 * English only, like the rest of the settings screens (Belal, 2026-10-20).
 * Socketless; open to every company user, refused to admins by the server.
 */
import { Check, Loader2, Search } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Header } from '@/components/header';
import {
  Commodity,
  BusinessProfile as Profile,
  ProfileField,
  ProfileValues,
  fetchBusinessProfile,
  saveBusinessProfile
} from '@/components/know-your-data/api';
import { groupCatalog } from '@/components/know-your-data/catalog';
import { SidebarProvider } from '@/components/ui/sidebar';

const surface = {
  background: 'var(--sig-surface)',
  borderColor: 'var(--sig-border)'
};

const T = {
  en: {
    eyebrow: 'Business profile',
    title: 'Your business',
    intro:
      'Signals uses this to pick the market prices and events that matter to your business. Nothing here is required, and you can change it any time.',
    suggested: 'Suggested from your data',
    customer: 'You confirmed',
    team: 'Set by BuduCloud',
    waiting: 'Waiting for your answer',
    industry: 'Field of business',
    pickIndustry: 'Pick one',
    commodities: 'Materials you buy that have a world price',
    commoditiesHint: (n: number) =>
      `The first ${n} you pick are compared with your costs on every cost question. The rest are used when you ask about them.`,
    search: 'Search world prices',
    browse: (n: number) => `Browse all ${n} world prices`,
    hideBrowse: 'Hide the list',
    add: 'Add',
    added: 'Added',
    full: (n: number) => `Up to ${n}`,
    notUpdated: (m: string) => `Not updated since ${m}`,
    yearAgo: (pct: string) => `${pct} on a year ago`,
    auto: 'auto',
    fromGroups: 'From your item groups:',
    noMatch:
      'No world price matches. Not every material has one (dairy, for example).',
    save: 'Save',
    useSuggestion: 'Use these',
    imports: 'How much of what you buy is imported?',
    importsWhy:
      'So Signals knows how much weight to give the dollar in answers about cost.',
    fuel: 'Do you run your own delivery vehicles?',
    fuelWhy: 'If you do, fuel prices reach your costs directly.',
    weather: 'Does heat or cold change your sales?',
    weatherWhy: 'If it does, Signals compares your sales with the weather.',
    tourism: 'Do hotels, restaurants or tourists buy from you?',
    tourismWhy: 'If they do, Signals watches tourism figures too.',
    usd: 'Do your selling prices go up and down with the US dollar?',
    usdWhy: 'If they do, Signals checks the dollar first when your sales move.',
    credit: 'Do you sell on credit or in instalments?',
    creditWhy:
      'If you do, interest rates change what your customers can buy, so Signals checks them first when your sales move.',
    debt: 'Do you have bank loans or an overdraft?',
    debtWhy:
      'If you do, interest rates reach your costs directly, so Signals checks them first when your expenses move.',
    watches: 'Signals will watch:',
    yes: 'Yes',
    no: 'No',
    saved: 'Saved',
    lastChange: (who: string, when: string) =>
      `Last saved by ${who} on ${when}.`,
    everyone: 'Applies to everyone in your company.'
  }
};

export default function BusinessProfile() {
  const navigate = useNavigate();
  const [data, setData] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<ProfileField | null>(null);

  useEffect(() => {
    setError(null);
    fetchBusinessProfile('en')
      .then(setData)
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Could not load this page.')
      );
  }, []);

  const t = T.en;
  const save = (field: ProfileField, value: ProfileValues[ProfileField]) => {
    if (!data) return;
    setSaving(field);
    saveBusinessProfile({ [field]: value }, 'en')
      .then((p) =>
        setData((old) =>
          old
            ? {
                ...old,
                values: p.values,
                sources: p.sources,
                pending: p.pending,
                updated_at: p.updated_at,
                updated_by: p.updated_by
              }
            : p
        )
      )
      .then(() => toast.success(t.saved))
      .catch((e) =>
        toast.error(e instanceof Error ? e.message : 'Could not save.')
      )
      .finally(() => setSaving(null));
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full flex-col">
        <Header />
        <div className="flex-1 overflow-auto">
          <div
            className="mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-8"
            dir="ltr"
            lang="en"
          >
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <p
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--sig-blue)' }}
                >
                  {t.eyebrow}
                </p>
                <h1 className="text-2xl font-semibold text-[var(--sig-ink)]">
                  {t.title}
                </h1>
                <p className="text-sm text-muted-foreground">{t.intro}</p>
              </div>
            </div>

            {error ? (
              <div className="rounded-xl border p-4 text-sm" style={surface}>
                {error}
              </div>
            ) : !data ? (
              <div className="grid place-items-center py-16">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-3">
                <Field
                  title={t.industry}
                  state={stateOf(data, 'industry')}
                  t={t}
                >
                  <select
                    id="bp-industry"
                    className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={data.values.industry ?? ''}
                    disabled={saving === 'industry'}
                    onChange={(e) => save('industry', e.target.value || null)}
                  >
                    <option value="">{t.pickIndustry}</option>
                    {data.industries.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.label}
                      </option>
                    ))}
                    {data.values.industry &&
                    !data.industries.some(
                      (i) => i.value === data.values.industry
                    ) ? (
                      <option value={data.values.industry}>
                        {data.values.industry}
                      </option>
                    ) : null}
                  </select>
                </Field>

                {/* Hidden when it does not fit the field of business: a
                    language centre buys no material with a world price. */}
                {!data.hidden?.includes('input_commodities') ? (
                  <Commodities
                    data={data}
                    t={t}
                    saving={saving === 'input_commodities'}
                    onSave={(keys) => save('input_commodities', keys)}
                  />
                ) : null}

                <Field
                  title={t.imports}
                  why={t.importsWhy}
                  state={stateOf(data, 'import_share')}
                  watches={data.watches?.import_share}
                  t={t}
                >
                  <Choice
                    options={data.import_shares.map((o) => ({
                      value: o.value,
                      label: o.label
                    }))}
                    value={data.values.import_share}
                    disabled={saving === 'import_share'}
                    onPick={(v) =>
                      save('import_share', v as ProfileValues['import_share'])
                    }
                  />
                </Field>

                {(
                  [
                    ['prices_follow_usd', t.usd, t.usdWhy],
                    ['sells_on_credit', t.credit, t.creditWhy],
                    ['has_bank_debt', t.debt, t.debtWhy],
                    ['fuel_intensive', t.fuel, t.fuelWhy],
                    ['weather_sensitive', t.weather, t.weatherWhy],
                    ['hospitality_exposed', t.tourism, t.tourismWhy]
                  ] as [ProfileField, string, string][]
                )
                  // A question the company's country has no data for would
                  // promise figures the chat never reads; one that does not
                  // fit the field of business is not asked at all.
                  .filter(([field]) => !data.hidden?.includes(field))
                  .map(([field, title, why]) => (
                    <Field
                      key={field}
                      title={title}
                      why={why}
                      state={stateOf(data, field)}
                      watches={data.watches?.[field]}
                      t={t}
                    >
                      <Choice
                        options={[
                          { value: 'yes', label: t.yes },
                          { value: 'no', label: t.no }
                        ]}
                        value={
                          data.values[field] === null
                            ? null
                            : data.values[field]
                              ? 'yes'
                              : 'no'
                        }
                        disabled={saving === field}
                        onPick={(v) => save(field, v === 'yes')}
                      />
                    </Field>
                  ))}

                <div className="space-y-1 pb-6 pt-2 text-xs text-muted-foreground">
                  {data.updated_by && data.updated_at ? (
                    <p>
                      {t.lastChange(
                        data.updated_by,
                        new Date(data.updated_at).toLocaleDateString()
                      )}
                    </p>
                  ) : null}
                  <p>{t.everyone}</p>
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() => navigate('/know-your-data')}
                  >
                    Know your data →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

type State = 'customer' | 'team' | 'suggested' | 'waiting' | null;

function stateOf(data: Profile, field: ProfileField): State {
  const source = data.sources[field];
  if (source) return source;
  if (field === 'input_commodities' && data.suggested.length)
    return 'suggested';
  const asked = data.asked ?? [
    'input_commodities',
    'import_share',
    'fuel_intensive'
  ];
  if (asked.includes(field)) return 'waiting';
  return null;
}

function Pill({ state, t }: { state: State; t: (typeof T)['en'] }) {
  if (!state) return null;
  const look = {
    customer: {
      color: 'var(--sig-positive)',
      border: '1px solid var(--sig-positive)',
      background: 'var(--sig-surface)',
      text: `✓ ${t.customer}`
    },
    team: {
      color: 'var(--sig-muted)',
      border: '1px solid var(--sig-border)',
      background: 'var(--sig-table-head)',
      text: t.team
    },
    suggested: {
      color: 'hsl(var(--primary))',
      border: '1px dashed var(--sig-blue)',
      background: 'var(--sig-chip)',
      text: t.suggested
    },
    waiting: {
      color: 'var(--sig-warn-text)',
      border: '1px solid var(--sig-warn-border)',
      background: 'var(--sig-warn-bg)',
      text: t.waiting
    }
  }[state];
  return (
    <span
      className="shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{
        color: look.color,
        border: look.border,
        background: look.background
      }}
    >
      {look.text}
    </span>
  );
}

function Field({
  title,
  why,
  state,
  watches,
  t,
  children
}: {
  title: string;
  why?: string;
  state: State;
  watches?: string[];
  t: (typeof T)['en'];
  children: ReactNode;
}) {
  return (
    <section className="space-y-2.5 rounded-xl border p-4" style={surface}>
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-[15px] font-semibold leading-snug text-[var(--sig-ink)]">
          {title}
        </h2>
        <Pill state={state} t={t} />
      </div>
      {why ? <p className="text-xs text-muted-foreground">{why}</p> : null}
      {children}
      {watches?.length ? (
        <p className="text-xs text-[var(--sig-body)]">
          <span className="font-medium">{t.watches}</span> {watches.join(' · ')}
        </p>
      ) : null}
    </section>
  );
}

function Choice({
  options,
  value,
  disabled,
  onPick
}: {
  options: { value: string; label: string }[];
  value: string | null;
  disabled: boolean;
  onPick: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2" role="group">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={disabled}
          aria-pressed={value === o.value}
          onClick={() => onPick(o.value)}
          className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors disabled:opacity-60 ${
            value === o.value
              ? 'border-primary bg-primary text-primary-foreground'
              : 'hover:bg-muted'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Commodities({
  data,
  t,
  saving,
  onSave
}: {
  data: Profile;
  t: (typeof T)['en'];
  saving: boolean;
  onSave: (keys: string[]) => void;
}) {
  const saved = useMemo(
    () => data.values.input_commodities ?? [],
    [data.values.input_commodities]
  );
  const start = saved.length ? saved : data.suggested.map((s) => s.key);
  const [picked, setPicked] = useState<string[]>(start);
  const [q, setQ] = useState('');
  const [browse, setBrowse] = useState(false);
  useEffect(
    () => setPicked(saved.length ? saved : data.suggested.map((s) => s.key)),
    [saved, data.suggested]
  );

  const names = useMemo(
    () => Object.fromEntries(data.commodities.map((c) => [c.key, c.name])),
    [data.commodities]
  );
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle
      ? data.commodities.filter(
          (c) =>
            c.name.toLowerCase().includes(needle) ||
            c.key.toLowerCase().includes(needle)
        )
      : data.commodities.filter(
          (c) =>
            picked.includes(c.key) ||
            data.suggested.some((s) => s.key === c.key)
        );
    return list.slice(0, 40);
  }, [q, data.commodities, data.suggested, picked]);
  // The catalog: every world price, grouped, with what it is and its price.
  // Opened by "Browse all" or by typing (catalog.ts).
  const catalog = useMemo(
    () => groupCatalog(data.commodities, q),
    [q, data.commodities]
  );
  const showCatalog = browse || !!q.trim();
  const changed = picked.join('|') !== saved.join('|');
  const isSuggestion =
    !saved.length &&
    picked.join('|') === data.suggested.map((x) => x.key).join('|');

  const toggle = (key: string) =>
    setPicked((p) =>
      p.includes(key)
        ? p.filter((k) => k !== key)
        : p.length >= data.max_commodities
          ? p
          : [...p, key]
    );

  return (
    <Field
      title={t.commodities}
      why={t.commoditiesHint(data.auto_tested)}
      state={stateOf(data, 'input_commodities')}
      t={t}
    >
      {picked.length ? (
        <div className="flex flex-wrap gap-1.5">
          {picked.map((key, i) => (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary px-3 py-1 text-sm text-primary-foreground"
            >
              {names[key] ?? key}
              {i < data.auto_tested ? (
                <span className="text-[10px] opacity-80">· {t.auto}</span>
              ) : null}
              <span aria-hidden="true" className="ms-1 opacity-70">
                ×
              </span>
            </button>
          ))}
        </div>
      ) : null}
      <label className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2">
        <Search className="size-4 text-muted-foreground" />
        <input
          id="bp-commodity-search"
          className="w-full bg-transparent text-sm outline-none"
          placeholder={t.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </label>
      <button
        type="button"
        onClick={() => setBrowse((b) => !b)}
        className="text-sm font-medium text-primary hover:underline"
      >
        {browse ? t.hideBrowse : t.browse(data.commodities.length)}
      </button>
      {showCatalog ? (
        <div className="max-h-[32rem] space-y-4 overflow-y-auto pe-1">
          {catalog.map(({ group, items }) => (
            <div key={group} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {items.map((c) => (
                  <CatalogCard
                    key={c.key}
                    c={c}
                    t={t}
                    picked={picked.includes(c.key)}
                    full={picked.length >= data.max_commodities}
                    max={data.max_commodities}
                    onToggle={() => toggle(c.key)}
                  />
                ))}
              </div>
            </div>
          ))}
          {!catalog.length ? (
            <p className="text-xs text-muted-foreground">{t.noMatch}</p>
          ) : null}
        </div>
      ) : null}
      <div className={`flex flex-wrap gap-1.5 ${showCatalog ? 'hidden' : ''}`}>
        {shown
          .filter((c) => !picked.includes(c.key))
          .map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => toggle(c.key)}
              className="rounded-full border px-3 py-1 text-sm hover:bg-muted"
            >
              {c.name}
            </button>
          ))}
      </div>
      {data.suggested.length ? (
        <div
          className="space-y-0.5 rounded-lg px-3 py-2 text-xs text-[var(--sig-body)]"
          style={{ background: 'var(--sig-table-head)' }}
        >
          {data.suggested.map((s) => (
            <p key={s.key}>
              <b className="text-[var(--sig-ink)]">{s.name}:</b> {t.fromGroups}{' '}
              {s.groups.map((g, i) => (
                <span key={g}>
                  {i ? ', ' : null}
                  <bdi>{g}</bdi>
                </span>
              ))}
            </p>
          ))}
        </div>
      ) : null}
      {changed && picked.length + saved.length > 0 ? (
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave(picked)}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        >
          {saving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
          {isSuggestion ? t.useSuggestion : t.save}
        </button>
      ) : null}
    </Field>
  );
}

function CatalogCard({
  c,
  t,
  picked,
  full,
  max,
  onToggle
}: {
  c: Commodity;
  t: (typeof T)['en'];
  picked: boolean;
  full: boolean;
  max: number;
  onToggle: () => void;
}) {
  const month = c.month
    ? new Date(`${c.month}-01T00:00:00`).toLocaleDateString('en-GB', {
        month: 'short',
        year: 'numeric'
      })
    : null;
  const price =
    c.last == null
      ? null
      : c.last.toLocaleString('en-US', {
          maximumFractionDigits: Math.abs(c.last) < 100 ? 2 : 0
        });
  const change =
    c.change_1y == null
      ? null
      : `${c.change_1y > 0 ? '+' : ''}${Math.round(c.change_1y * 100)}%`;
  return (
    <div
      className={`space-y-1.5 rounded-xl border p-3 ${picked ? 'border-primary' : ''}`}
      style={picked ? undefined : surface}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-[var(--sig-ink)]">
          {c.name}
        </span>
        <button
          type="button"
          onClick={onToggle}
          disabled={!picked && full}
          title={!picked && full ? t.full(max) : undefined}
          className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium disabled:opacity-50 ${
            picked
              ? 'border-primary bg-primary text-primary-foreground'
              : 'hover:bg-muted'
          }`}
        >
          {picked ? <Check className="size-3" /> : null}
          {picked ? t.added : t.add}
        </button>
      </div>
      {c.about ? (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {c.about}
        </p>
      ) : null}
      <div className="flex items-end justify-between gap-2">
        <p className="text-xs text-[var(--sig-body)]">
          {c.stale ? (
            month ? (
              t.notUpdated(month)
            ) : null
          ) : price ? (
            <>
              <b className="text-[var(--sig-ink)]">{price}</b> {c.unit}
              {month ? ` · ${month}` : null}
              {change ? (
                <span className="block text-muted-foreground">
                  {t.yearAgo(change)}
                </span>
              ) : null}
            </>
          ) : null}
        </p>
        {!c.stale && c.spark && c.spark.length > 1 ? (
          <Spark values={c.spark} />
        ) : null}
      </div>
    </div>
  );
}

/** The last 13 monthly prices, oldest on the left. */
function Spark({ values }: { values: number[] }) {
  const w = 72;
  const h = 22;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const points = values
    .map(
      (v, i) =>
        `${((i / (values.length - 1)) * w).toFixed(1)},${(h - 2 - ((v - lo) / span) * (h - 4)).toFixed(1)}`
    )
    .join(' ');
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
      className="shrink-0"
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--sig-blue)"
        strokeWidth="1.5"
      />
    </svg>
  );
}
